<template>
  <section>
    <div class="mb-2">
      <b-row
        v-if="showAddItem"
        class="mb-1"
      >
        <b-col class="d-flex justify-content-end">
          <e-button
            :text="$t('Adicionar item')"
            variant="primary"
            @click="onAddItem"
          />
        </b-col>
      </b-row>

      <b-row>
        <b-col>
          <FormulateForm ref="formulateInvoiceItems">
            <b-table
              ref="productTable"
              show-empty
              striped
              class="bordered"
              :fields="fields"
              :items="items"
              style="max-height: 500px !important"
            >
              <template #cell(erpSku_name)="{ item, index }">
                <div class="d-flex flex-column gap-4">
                  <div
                    v-if="!localReadOnly && (!item.skuId || !item.sku)"
                    style="display: flex; margin-right: 5px"
                  >
                    <div style="width: 100%">
                      <FormulateInput
                        :id="`invoice-items-sku-searched-${index}`"
                        v-model="item.skuId"
                        type="select-searchable"
                        :placeholder="$t('Digite para pesquisar')"
                        :options="skusOptions"
                        autofocus
                        :instruction="$t('Digite pelo menos 3 numeros ou letras para iniciar a pesquisa')
                        "
                        style="width: 100%; min-width: 200px; display: inline-block"
                        class="ml-50"
                        validation="required"
                        :validation-messages="{
                          required: $t('O produto é obrigatório'),
                        }"
                        @fetchSearch="fetchSearchSku"
                        @input="selectSku(item, item.skuId)"
                      />
                    </div>
                  </div>
                  <div
                    v-else
                    style="display: flex"
                  >
                    <div style="display: flex; align-items: center; margin: 0 10px">
                      <e-button
                        icon="journal-text"
                        variant="primary"
                        class="p-5"
                        :title="$t('Dados fiscais do produto')"
                        @click="onShowItemDataModal(item)"
                      />
                    </div>
                    <e-product-router-link
                      :sku-id="item.sku.id"
                      :product-id="item.sku.productId"
                      :product-name="item.sku.name"
                      :ean="item.sku.ean"
                    />
                  </div>

                  <div
                    v-if="showReferenceToPurchaseOrderItem && item.skuId"
                    style="margin: 0 10px"
                  >
                    <div
                      v-if="isReadOnly || getItemReferenceInfo(item).hasReference"
                      class="d-flex gap-3"
                    >
                      <div v-if="!(isReadOnly || isDevolution || isInvoiceFromInventory)">
                        <e-button
                          size="sm"
                          variant="danger"
                          class="p-5"
                          icon="x"
                          :title="$t('Remover referência')"
                          @click="() => onRemoveItemReference(item)"
                        />
                      </div>
                      <div>
                        <p
                          v-if="getItemReferenceInfo(item).hasReference"
                          class="font-weight-bold"
                        >
                          <FormulateInput
                            :value="getItemReferenceInfo(item).label"
                            type="label"
                            :label="$t('Item Referenciado')"
                            :router-to="getItemReferenceInfo(item).routerTo"
                          />
                        </p>
                        <p v-else>
                          Nenhum item referenciado
                        </p>
                      </div>
                    </div>
                    <div v-else>
                      <e-button
                        :text="$t('Referenciar item')"
                        size="sm"
                        variant="warning"
                        @click="() => onPurchaseReferenceModalShow(item)"
                      />
                    </div>
                  </div>
                </div>
              </template>

              <template #cell(erpSku_ean)="row">
                <span>{{ row.item.sku ? row.item.sku.ean : '' }}</span>
              </template>

              <template #cell(erpSku_unitOfMeasurement)="row">
                <span>{{
                  row.item.sku
                    ? row.item.sku.unitOfMeasurement || row.item.unit || '-'
                    : row.item.unit || '-'
                }}</span>
              </template>

              <template #cell(cfop)="row">
                <FormulateInput
                  v-if="canEditCfop"
                  :id="`invoice-items-cfop-${row.index}`"
                  v-model="row.item.cfop"
                  type="vue-select"
                  :options="cfopOptionsByNatureOperation"
                  :title="$t('Clique para editar')"
                  validation="required"
                  :clearable="false"
                  @input="onUpdateItem"
                />
                <span v-else>{{ row.item.cfop }}</span>
              </template>

              <template
                v-if="isStockLossesCategory"
                #cell(stockLossesReason)="row"
              >
                <FormulateInput
                  v-if="!localReadOnly"
                  :id="`invoice-items-loss-reason-${row.index}`"
                  v-model="row.item.stockLossesReason"
                  type="vue-select"
                  :options="invoiceStockLossesReasonOptions()"
                  :class="{ required: isStockLossesCategory }"
                  :title="$t('Clique para editar')"
                  :validation="isStockLossesCategory ? 'required' : ''"
                  :clearable="false"
                  @input="(value) => onUpdateLossesReason(value, row.item)"
                />
                <span v-else>{{ invoiceStockLossesReasonLabel[row.item.stockLossesReason] }}</span>
              </template>

              <template #cell(erpSku_quantity)="row">
                <FormulateInput
                  v-if="canEditErpSkuQuantity && !isItemWithReference(row.item)"
                  :id="`invoice-items-quantity-${row.index}`"
                  v-model="row.item.quantity"
                  type="text-number"
                  :precision="3"
                  class="text-right"
                  :title="$t('Clique para editar')"
                  validation="required|min:1"
                  :validation-messages="{
                    required: $t('Obrigatório'),
                    min: $t('Deve ser maior que 1'),
                  }"
                  @blur="onUpdateItem"
                />
                <div v-else>
                  <span v-if="canEditErpSkuQuantity && isItemWithReference(row.item)">
                    <e-instructions
                      container-class="mr-0"
                      :title="$t('Informação')"
                      :target="`instruction_item_quantity_info_${row.index}`"
                      :instruction="`${$t(
                        'Para alterar a quantidade, necessário remover o item referenciado.'
                      )}`"
                    />
                    <br>
                  </span>
                  <span>
                    {{ row.item.quantity | number(3) }}
                  </span>
                </div>
              </template>

              <template #cell(erpSku_unitValue)="row">
                <template v-if="isXml || isItemWithReference(row.item)">
                  {{ (row.item.totalValue / row.item.quantity) | currency }}
                </template>
                <template v-else>
                  <FormulateInput
                    v-if="canEditUnitPrice"
                    :id="`invoice-items-unit_price-${row.index}`"
                    v-model="row.item.unityPrice"
                    type="text-number"
                    currency="R$"
                    :precision="5"
                    class="text-right"
                    :title="$t('Clique para editar')"
                    validation="required|min:0.01"
                    :validation-messages="{
                      required: $t('Obrigatório'),
                      min: $t('Deve ser maior que 0.01'),
                    }"
                    style="width: 120px"
                    @blur="onUpdateItem"
                  />
                  <span v-else>
                    {{ row.item.unityPrice | currency }}
                  </span>
                </template>
              </template>

              <template #cell(aditionalInformation)="row">
                <FormulateInput
                  v-if="!localReadOnly && !isInvoiceComplementary"
                  :id="`invoice-items-additional_information-${row.index}`"
                  v-model="row.item.aditionalInformation"
                  type="textarea"
                  :title="$t('Informação adicional')"
                  style="width: 120px"
                  :rows="5"
                  :validation="isDebitForStockLosses ? '^required|min:10,length' : ''"
                  :validation-messages="{
                    required: $t('Obrigatório informar motivo da baixa'),
                    min: $t('Dever ter mais de 10 caracteres'),
                  }"
                />
                <span v-else>
                  {{ row.item.aditionalInformation || '-' }}
                </span>
              </template>

              <template #cell(fiscal)="row">
                <b>{{ $t('NCM') }}:</b> {{ row.item.ncm }}<br>
                <b>{{ $t('CEST') }}:</b> {{ row.item.cest }}<br>
                <b>{{ $t('CST ICMS') }}:</b> {{ row.item.stIcms }}<br>
                <b>{{ $t('CST PIS') }}:</b> {{ row.item.stPis }}<br>
                <b>{{ $t('CST COFINS') }}:</b> {{ row.item.stCofins }}<br>
                <e-button
                  v-if="!isInvoiceComplementary"
                  style="color: blue"
                  class="p-0"
                  variant="link"
                  :text="$t(!row.detailsShowing ? 'Ver mais' : 'Recolher')"
                  @click="row.toggleDetails"
                >{{
                  row.detailsShowing ? 'Hide' : 'Show' }}</e-button>
              </template>

              <template #row-details="row">
                <div style="margin-left: 80px">
                  <b-row class="mb-1">
                    <b-col md="2">
                      <FormulateInput
                        :id="`icms-base-${row.index}`"
                        v-model="row.item.icmsBase"
                        :label="$t('Base de ICMS')"
                        type="text-number"
                        :disabled="!isInvoiceComplementary || localReadOnly"
                        :precision="2"
                        @blur="onUpdateItem"
                      />
                    </b-col>
                    <b-col md="2">
                      <FormulateInput
                        :id="`icms-percent-${row.index}`"
                        v-model="row.item.icmsPercent"
                        :label="$t('Alíquota de ICMS')"
                        type="text-percetage"
                        currency="%"
                        :disabled="!isInvoiceComplementary || localReadOnly"
                        :precision="4"
                        @blur="onUpdateItem"
                      />
                    </b-col>
                    <b-col md="2">
                      <FormulateInput
                        :id="`icms-value-${row.index}`"
                        v-model="row.item.icmsValue"
                        :label="$t('Valor de ICMS')"
                        type="text-number"
                        :disabled="!isInvoiceComplementary || localReadOnly"
                        :precision="2"
                        @blur="onUpdateItem"
                      />
                    </b-col>
                    <b-col md="2">
                      <FormulateInput
                        :id="`fcp-value-${row.index}`"
                        v-model="row.item.fcpValue"
                        :label="$t('Valor do FCP')"
                        type="text-number"
                        :disabled="!isInvoiceComplementary || localReadOnly"
                        :precision="2"
                        @blur="onUpdateItem"
                      />
                    </b-col>
                  </b-row>
                  <b-row class="mb-1">
                    <b-col md="2">
                      <FormulateInput
                        :id="`icms-st-base-${row.index}`"
                        v-model="row.item.icmsStBase"
                        :label="$t('Base de ICMS ST')"
                        type="text-number"
                        :disabled="!isInvoiceComplementary || localReadOnly"
                        :precision="2"
                        @blur="onUpdateItem"
                      />
                    </b-col>
                    <b-col md="2">
                      <FormulateInput
                        :id="`icms-st-percent-${row.index}`"
                        v-model="row.item.icmsStPercent"
                        :label="$t('Alíquota de ICMS ST')"
                        type="text-percetage"
                        :disabled="!isInvoiceComplementary || localReadOnly"
                        currency="%"
                        :precision="4"
                        @blur="onUpdateItem"
                      />
                    </b-col>
                    <b-col md="2">
                      <FormulateInput
                        :id="`icms-st-iva-${row.index}`"
                        v-model="row.item.iva"
                        :label="$t('IVA')"
                        type="text-percetage"
                        :disabled="!isInvoiceComplementary || localReadOnly"
                        currency="%"
                        :precision="4"
                        @blur="onUpdateItem"
                      />
                    </b-col>
                    <b-col md="2">
                      <FormulateInput
                        :id="`icms-st-value-${row.index}`"
                        v-model="row.item.icmsStValue"
                        :label="$t('Valor de ICMS ST')"
                        type="text-number"
                        :disabled="!isInvoiceComplementary || localReadOnly"
                        :precision="2"
                        @blur="onUpdateItem"
                      />
                    </b-col>
                    <b-col md="2">
                      <FormulateInput
                        :id="`fcp-st-value-${row.index}`"
                        v-model="row.item.fcpStValue"
                        :label="$t('Valor do FCP ST')"
                        type="text-number"
                        :disabled="!isInvoiceComplementary || localReadOnly"
                        :precision="2"
                        @blur="onUpdateItem"
                      />
                    </b-col>
                  </b-row>
                  <b-row class="mb-1">
                    <b-col md="2">
                      <FormulateInput
                        :id="`st-cbs-ibs-${row.index}`"
                        v-model="row.item.stCbsIbsCode"
                        :label="$t('ST CBS/IBS')"
                        type="text"
                        :disabled="!isInvoiceComplementary || localReadOnly"
                        :precision="2"
                        @blur="onUpdateItem"
                      />
                    </b-col>
                    <b-col md="2">
                      <FormulateInput
                        :id="`cclasstrib-cbs-ibs-${row.index}`"
                        v-model="row.item.fiscalClassificationCbsIbsCode"
                        :label="$t('cClassTrib CBS/IBS')"
                        type="text"
                        :disabled="!isInvoiceComplementary || localReadOnly"
                        :precision="2"
                        @blur="onUpdateItem"
                      />
                    </b-col>
                    <b-col md="2">
                      <b-dropdown
                        boundary="viewport"
                        variant="outline-light"
                        no-caret
                        class="no-padding w-100"
                      >
                        <b-dropdown-header>
                          Grupo CBS
                        </b-dropdown-header>
                        <b-dropdown-divider />
                        <template #button-content>
                          <div class="text-left w-100">
                            <FormulateInput
                              :id="`cbs-value-${row.index}`"
                              v-model="row.item.cbsValue"
                              :label="$t('CBS')"
                              type="text-number"
                              :disabled="!isInvoiceComplementary || localReadOnly"
                              :precision="4"
                              :instruction="$t('Expandir para ver alíquotas e reduções do CBS')"
                              @blur="onUpdateItem"
                            />
                          </div>
                        </template>
                        <b-dropdown-item>
                          <FormulateInput
                            :id="`cbs-percent-${row.index}`"
                            v-model="row.item.cbsPercent"
                            :label="$t('CBS %')"
                            type="text-percetage"
                            :disabled="!isInvoiceComplementary || localReadOnly"
                            :precision="4"
                            @blur="onUpdateItem"
                          />
                        </b-dropdown-item>
                        <b-dropdown-item>
                          <FormulateInput
                            :id="`cbs-reduction-${row.index}`"
                            v-model="row.item.cbsReductionPercent"
                            :label="$t('CBS Redução %')"
                            type="text-percetage"
                            :disabled="!isInvoiceComplementary || localReadOnly"
                            :precision="4"
                            @blur="onUpdateItem"
                          />
                        </b-dropdown-item>
                        <b-dropdown-item>
                          <FormulateInput
                            :id="`cbs-effective-${row.index}`"
                            v-model="row.item.cbsEffectivePercent"
                            :label="$t('CBS Efetivo %')"
                            type="text-percetage"
                            :disabled="!isInvoiceComplementary || localReadOnly"
                            :precision="4"
                            @blur="onUpdateItem"
                          />
                        </b-dropdown-item>
                      </b-dropdown>
                    </b-col>
                    <b-col md="2">
                      <b-dropdown
                        boundary="viewport"
                        variant="outline-light"
                        no-caret
                        class="no-padding w-100"
                      >
                        <b-dropdown-header>
                          Grupo IBS estadual
                        </b-dropdown-header>
                        <b-dropdown-divider />
                        <template #button-content>
                          <div class="text-left w-100">
                            <FormulateInput
                              :id="`ibs-uf-${row.index}`"
                              v-model="row.item.ibsStateValue"
                              :label="$t('IBS UF')"
                              type="text-number"
                              :disabled="!isInvoiceComplementary || localReadOnly"
                              :precision="4"
                              :instruction="$t('Expandir para ver alíquotas e reduções do IBS estadual')"
                              @blur="onUpdateItem"
                            />
                          </div>
                        </template>
                        <b-dropdown-item>
                          <FormulateInput
                            :id="`ibs-uf-percent-${row.index}`"
                            v-model="row.item.ibsStatePercent"
                            :label="$t('IBS %')"
                            type="text-percetage"
                            :disabled="!isInvoiceComplementary || localReadOnly"
                            :precision="4"
                            @blur="onUpdateItem"
                          />
                        </b-dropdown-item>
                        <b-dropdown-item>
                          <FormulateInput
                            :id="`ibs-uf-reduction-${row.index}`"
                            v-model="row.item.ibsStateReductionPercent"
                            :label="$t('IBS Redução %')"
                            type="text-percetage"
                            :disabled="!isInvoiceComplementary || localReadOnly"
                            :precision="4"
                            @blur="onUpdateItem"
                          />
                        </b-dropdown-item>
                        <b-dropdown-item>
                          <FormulateInput
                            :id="`ibs-uf-effective-${row.index}`"
                            v-model="row.item.ibsStateEffectivePercent"
                            :label="$t('IBS Efetivo %')"
                            type="text-percetage"
                            :disabled="!isInvoiceComplementary || localReadOnly"
                            :precision="4"
                            @blur="onUpdateItem"
                          />
                        </b-dropdown-item>
                      </b-dropdown>
                    </b-col>
                    <b-col md="2">
                      <b-dropdown
                        boundary="viewport"
                        variant="outline-light"
                        no-caret
                        class="no-padding w-100"
                      >
                        <b-dropdown-header>
                          Grupo IBS municipal
                        </b-dropdown-header>
                        <b-dropdown-divider />
                        <template #button-content>
                          <div class="text-left w-100">
                            <FormulateInput
                              :id="`ibs-mun-${row.index}`"
                              v-model="row.item.ibsMunicipalValue"
                              :label="$t('IBS MUN')"
                              type="text-number"
                              :disabled="!isInvoiceComplementary || localReadOnly"
                              :precision="4"
                              :instruction="$t('Expandir para ver alíquotas e reduções do IBS municipal')"
                              @blur="onUpdateItem"
                            />
                          </div>
                        </template>
                        <b-dropdown-item>
                          <FormulateInput
                            :id="`ibs-mun-percent-${row.index}`"
                            v-model="row.item.ibsMunicipalPercent"
                            :label="$t('IBS %')"
                            type="text-percetage"
                            :disabled="!isInvoiceComplementary || localReadOnly"
                            :precision="4"
                            @blur="onUpdateItem"
                          />
                        </b-dropdown-item>
                        <b-dropdown-item>
                          <FormulateInput
                            :id="`ibs-mun-reduction-${row.index}`"
                            v-model="row.item.ibsMunicipalReductionPercent"
                            :label="$t('IBS Redução %')"
                            type="text-percetage"
                            :disabled="!isInvoiceComplementary || localReadOnly"
                            :precision="4"
                            @blur="onUpdateItem"
                          />
                        </b-dropdown-item>
                        <b-dropdown-item>
                          <FormulateInput
                            :id="`ibs-mun-effective-${row.index}`"
                            v-model="row.item.ibsMunicipalEffectivePercent"
                            :label="$t('IBS Efetivo %')"
                            type="text-percetage"
                            :disabled="!isInvoiceComplementary || localReadOnly"
                            :precision="4"
                            @blur="onUpdateItem"
                          />
                        </b-dropdown-item>
                      </b-dropdown>
                    </b-col>
                  </b-row>
                  <b-row v-if="isDebitForStockLosses">
                    <b-col md="2">
                      <FormulateInput
                        :id="`cbs-estorno-cred-${row.index}`"
                        v-model="row.item.cbsCreditReversalValue"
                        :label="$t('Estorno de crédito CBS')"
                        type="text-number"
                        disabled
                        :precision="4"
                        @blur="onUpdateItem"
                      />
                    </b-col>
                    <b-col md="2">
                      <FormulateInput
                        :id="`ibs-estorno-cred-${row.index}`"
                        v-model="row.item.ibsCreditReversalValue"
                        :label="$t('Estorno de crédito IBS')"
                        type="text-number"
                        disabled
                        :precision="4"
                        @blur="onUpdateItem"
                      />
                    </b-col>
                  </b-row>
                </div>
              </template>

              <template #cell(action)="row">
                <e-grid-actions
                  :show-update="false"
                  :show-delete="showRemoveItem"
                  @delete="onRemoveItem(row.item)"
                />
              </template>
            </b-table>
          </FormulateForm>
        </b-col>
      </b-row>
    </div>

    <purchase-item-for-reference-modal
      v-if="showReferenceToPurchaseOrderItem"
      ref="purchaseItemForReferenceModal"
      :purchase-order-ids="purchaseOrderIdsReference"
      @items-selected="onPurchaseReferenceSelectItem"
    />

    <invoice-item-data-sidebar
      ref="invoiceItemDataSidebar"
      :is-read-only="localReadOnly"
      @update-item-tax="onUpdateItemTax"
    />
  </section>
</template>

<script>
import { BRow, BCol, BTable, BDropdown, BDropdownItem, BDropdownHeader, BDropdownDivider } from 'bootstrap-vue'
import { EButton, EProductRouterLink, EGridActions, EInstructions } from '@/views/components'
import { invoiceDomains, alerts } from '@/mixins'
import { mapGetters, mapState } from 'vuex'
import InvoiceItemDataSidebar from './InvoiceItemDataSidebar.vue'
import PurchaseItemForReferenceModal from './PurchaseItemForReferenceModal.vue'

export default {
  name: 'ItemsFormTable',

  components: {
    BRow,
    BCol,
    BTable,
    EButton,
    EGridActions,
    InvoiceItemDataSidebar,
    PurchaseItemForReferenceModal,
    EProductRouterLink,
    BDropdown,
    BDropdownItem,
    BDropdownHeader,
    BDropdownDivider,
    EInstructions
  },

  mixins: [alerts, invoiceDomains],

  props: {
    isXml: {
      type: Boolean,
      default: false,
    },
    invoice: {
      type: Object,
      required: true,
    },
    isReadOnly: {
      type: Boolean,
      default: false,
    },
    isReturn: {
      type: Boolean,
      default: false,
    },
    isOutput: {
      type: Boolean,
      default: true,
    },
    isInvoiceComplementary: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      saving: false,
      productOptions: [],
      updateCellIndex: null,
      sku: null,
      skus: [],
      hasFiscalTaxParametersError: false,
    }
  },

  computed: {
    ...mapState('pages/invoice/invoice/invoiceMaintain', ['natureOperationCfop']),
    ...mapGetters('pages/invoice', [
      'getStatesCanReferenceInItemForReturn',
    ]),
    ...mapGetters('pages/invoice/taxClassifications', [
      'getComboAllCfop',
      'getComboCfopOutput',
      'getComboCfopInput',
    ]),
    ...mapGetters('pages/invoice/invoice/invoiceMaintain', [
      'isDebitForStockLosses',
      'isDevolution',
      'isGeneratingInReadOnlyMode',
      'isInvoiceFromCouponCategory',
      'isInvoiceReturnFromCouponCategory',
      'isStockLossesCategory',
      'isInvoiceFromInventory',
    ]),

    localReadOnly() {
      return this.isReadOnly || this.isGeneratingInReadOnlyMode
    },

    purchaseOrderIdsReference() {
      return this.items
        .map(i => i.invoiceItemReference?.purchaseOrderItem?.purchaseOrderId)
        .filter(Boolean)
    },

    skusOptions() {
      return this.skus.map(sku => ({
        ...sku,
        value: sku.id,
        label: sku.name,
      }))
    },

    cfopOptionsByNatureOperation() {
      const selectedNatureCfop = this.natureOperationCfop
      const optionsAux = this.isOutput ? this.getComboCfopOutput : this.getComboCfopInput
      if (this.isReadOnly) return optionsAux

      if (!selectedNatureCfop?.code) return optionsAux

      const hasInNatureCfopRelated = cfop =>
        selectedNatureCfop.cfopRelatedCfops.some(rel => rel.cfopRelatedCode === cfop.code)
      const optionsAuxFiltered = optionsAux.filter(hasInNatureCfopRelated)
      if (optionsAuxFiltered.length === 0) return optionsAux

      // CFOP de ST da Natureza de Operação (e o inverso). Ex: 5152 <-> 5409
      const isNatureCfopStPair = cfop =>
        cfop.code === selectedNatureCfop.cfopStCode || cfop.cfopStCode === selectedNatureCfop.code

      return optionsAux.filter(cfop => hasInNatureCfopRelated(cfop) || isNatureCfopStPair(cfop))
    },

    showRemoveItem() {
      return !this.isReturn && !this.isInvoiceFromInventory
    },

    showAddItem() {
      return !(
        this.isXml ||
        this.localReadOnly ||
        this.isReturn ||
        this.isInvoiceReturnFromCouponCategory ||
        this.isInvoiceFromInventory
      )
    },

    canEditCfop() {
      return (
        !this.localReadOnly &&
        !this.isInvoiceComplementary &&
        !this.isInvoiceReturnFromCouponCategory
      )
    },
    canEditErpSkuQuantity() {
      return (
        !this.localReadOnly &&
        !this.isReturn &&
        !this.isInvoiceComplementary &&
        !this.isInvoiceReturnFromCouponCategory &&
        !this.isInvoiceFromInventory
      )
    },
    canEditUnitPrice() {
      return (
        !this.localReadOnly &&
        !this.isReturn &&
        !this.isInvoiceComplementary &&
        !this.isInvoiceReturnFromCouponCategory
      )
    },

    showReferenceToPurchaseOrderItem() {
      return this.isDebitForStockLosses ||
        (
          this.getStatesCanReferenceInItemForReturn.some(state => this.invoice?.store?.address?.province === state) &&
          this.isDevolution
        )
    },

    fields() {
      return [
        {
          label: this.$t('Ações'),
          key: 'action',
          class: 'text-center',
          thStyle: { width: '80px' },
          hide: this.isXml || this.localReadOnly || this.isReturn || this.isInvoiceFromInventory,
        },
        {
          label: this.$t('Produto'),
          key: 'xmlProduct.name',
          hide: !this.isXml,
        },
        {
          label: this.$t('Unid.'),
          key: 'xmlProduct.unitOfMeasurement',
          class: 'text-center',
          hide: !this.isXml,
        },
        {
          label: this.$t('Qtd.'),
          key: 'quantity',
          class: 'text-center',
          formatter: value => this.$options.filters.number(value, 3, true),
          hide: !this.isXml,
        },

        {
          label: this.$t('Produto (Cód. Barras)'),
          key: 'erpSku_name',
          thClass: 'echope-product',
        },
        {
          label: this.$t('Cód Barras'),
          key: 'erpSku_ean',
          thClass: 'echope-product',
          thStyle: { width: '80px' },
          hide: true,
        },
        {
          label: this.$t('Unid.'),
          key: 'erpSku_unitOfMeasurement',
          class: 'text-center',
          thClass: 'echope-product',
          thStyle: { width: '80px' },
        },
        {
          label: this.$t('Fiscal'),
          key: 'fiscal',
        },

        {
          label: this.$t('Ncm'),
          key: 'ncm',
          thClass: 'text-center',
          thStyle: { width: '100px' },
          hide: true,
        },
        {
          label: this.$t('Cest'),
          key: 'cest',
          thClass: 'text-center',
          tdClass: 'text-center',
          thStyle: { width: '100px' },
          hide: true,
        },
        {
          label: this.$t('Cfop'),
          key: 'cfop',
          thClass: 'text-center',
          tdClass: 'text-center',
          thStyle: { width: '80px' },
        },
        {
          label: this.$t('Qtd.'),
          key: 'erpSku_quantity',
          class: 'text-center',
          thClass: 'echope-product',
          thStyle: { width: '100px' },
          formatter: value => this.$options.filters.number(value, 3, true),
        },
        {
          label: this.$t('Motivo baixa'),
          key: 'stockLossesReason',
          thClass: 'text-center',
          tdClass: 'text-center',
          thStyle: { width: '140px' },
          hide: !this.isStockLossesCategory,
        },
        {
          label: this.$t('Informação Adicional'),
          key: 'aditionalInformation',
          thClass: 'text-center',
          tdClass: 'text-left',
          thStyle: { width: '110px' },
        },
        {
          label: this.$t('Vlr. Unit.'),
          key: 'erpSku_unitValue',
          class: 'text-center',
          thStyle: { width: '100px' },
        },
        {
          label: this.$t('Vlr. Total'),
          key: 'totalValue',
          class: 'text-center',
          thStyle: { width: '100px' },
          formatter: value => this.$options.filters.currency(value),
        },
        {
          label: this.$t('Cst ICMS'),
          key: 'stIcms',
          thClass: 'text-center',
          tdClass: 'text-cemter',
          thStyle: { width: '80px' },
          hide: true,
        },
        {
          label: this.$t('Cst PIS'),
          key: 'stPis',
          thClass: 'text-center',
          tdClass: 'text-cemter',
          thStyle: { width: '80px' },
          hide: true,
        },
        {
          label: this.$t('Cst COFINS'),
          key: 'stCofins',
          thClass: 'text-center',
          tdClass: 'text-cemter',
          thStyle: { width: '80px' },
          hide: true,
        },
      ].filter(a => !a.hide)
    },
    items() {
      return this.invoice?.items
        .filter(i => !i.isDeleted)
        .map(i => {
          // eslint-disable-next-line no-underscore-dangle, no-param-reassign
          i._stockLossesReason = i.stockLossesReason
          return i
        }
        )
    },
  },

  mounted() { },

  methods: {
    onUpdateLossesReason(value, item) {
      // eslint-disable-next-line no-underscore-dangle
      if (
        !this.localReadOnly &&
        this.isDebitForStockLosses &&
        // eslint-disable-next-line no-underscore-dangle
        value !== item._stockLossesReason
      ) {
        // eslint-disable-next-line no-param-reassign
        item.aditionalInformation = this.mxGetLossReasonAdditionInfo(value)
      }

      this.onUpdateItem()
    },

    isItemWithReference(item) {
      if (!this.isDebitForStockLosses) return false

      return item.invoiceItemReference && item.invoiceItemReference?.accessKey
    },

    createNewProduct(productItem, index) {
      this.$router.push({
        name: 'purchase-order-xml-import-add-product',
        params: {
          callbackRouteName: this.$route.name,
          purchaseOrderData: {
            invoice: this.invoice,
            itemIndex: index,
          },
        },
      })
    },

    getItemReferenceInfo(item) {
      const hasReference = !!item.invoiceItemReference?.accessKey
      let routerTo = null
      let label = ''

      if (hasReference) {
        label = `Chave de acesso: ${item.invoiceItemReference?.accessKey} - Item #${item.invoiceItemReference?.sequentialNumber}`

        const referenceInfo = item?.invoiceItemReference
        if (referenceInfo?.purchaseOrderItemId) {
          label = `Pedido #${referenceInfo?.purchaseOrderItem?.purchaseOrderId} - Item #${referenceInfo?.sequentialNumber}`
          routerTo = {
            name: 'purchase-order-details',
            params: {
              id: referenceInfo.purchaseOrderItem.purchaseOrderId
            },
          }
        } else if (item.referenceInvoiceItemId) {
          const invoice = item.referenceInvoiceItem?.invoice
          label = `${invoice.model} ${invoice?.invoiceNumber} / ${invoice?.serialNumber} - Item #${item.invoiceItemReference?.sequentialNumber}`
          routerTo = {
            name: 'invoice-maintain',
            params: {
              id: item.referenceInvoiceItem?.invoiceId
            },
          }
        }
      }

      return {
        ...(item.invoiceItemReference || {}),
        referenceInvoiceItemId: item.referenceInvoiceItemId,
        hasReference,
        label,
        routerTo
      }
    },

    async selectSku(item, skuIdSelected) {
      if (skuIdSelected) {
        const localItem = { ...item }
        localItem.sku = this.skus.find(sku => sku.id === parseInt(skuIdSelected, 10))

        this.$emit('update-product-item', localItem)
      }
    },

    async deleteSkuAssociation(item) {
      if (
        await this.confirm({
          icon: 'question',
          text: this.$t('Tem certeza que deseja remover esta associação?'),
        })
      ) {
        // eslint-disable-next-line no-param-reassign
        item.skuId = null
        // eslint-disable-next-line no-param-reassign
        item.sku = null
      }
    },

    async fetchSearchSku(loading, search) {
      try {
        loading(true)
        this.skus = (
          await this.$http.get('/api/skus/search', {
            params: {
              search,
              active: true,
              includeFixedAssets: true,
            },
          })
        ).data.results
      } catch (e) {
        this.showError({
          message: this.$t('Não foi possível fazer a pesquisa'),
        })
      } finally {
        loading(false)
      }
    },

    onShowItemDataModal(item) {
      this.$refs.invoiceItemDataSidebar.show(item)
    },

    onPurchaseReferenceModalShow(item) {
      this.$refs.purchaseItemForReferenceModal.show(this.invoice?.storeId, {
        skuId: item.skuId,
        sku: item.sku,
        invoiceItemId: item.id,
        quantity: item.quantity,
        localId: item.localId,
      })
    },
    onRemoveItemReference(item) {
      this.$emit('update-item', {
        ...item,
        invoiceItemReference: null
      })
    },

    onPurchaseReferenceSelectItem(selectedItems) {
      this.$emit('purchase-items-reference', selectedItems)
    },

    onAddItem() {
      if (this.invoice.storeId === null) {
        this.showWarning({
          title: this.$t('Loja não selecionada'),
          message: this.$t('Selecione a loja para adicionar os itens.'),
        })
        return
      }

      if (!this.natureOperationCfop) {
        this.showWarning({
          title: this.$t('Natureza da operação'),
          message: this.$t('Selecione a natureza da operação para adicionar os itens.'),
        })
        return
      }

      if (this.items.some(i => !i.skuId)) {
        this.showWarning({
          title: this.$t('Item sem produto'),
          message: this.$t('Preencha os campos de produto antes de adicionar um item.'),
        })
        return
      }

      this.$emit('add-item')
    },
    onUpdateItem() {
      this.$emit('update-invoice-item-totals')
    },
    onUpdateItemTax(item) {
      this.$emit('update-item', item)
    },
    async onRemoveItem(item) {
      const confirmed = await this.confirm({
        icon: 'question',
        text: this.$t('Tem certeza que deseja remover este item?'),
      })

      if (confirmed) {
        this.$emit('remove-item', item)
      }
    },

    validate() {
      this.$refs.formulateInvoiceItems.showErrors()
      const productMissing = this.items.some(i => !i.skuId)
      return this.$refs.formulateInvoiceItems.hasErrors || productMissing
    },
  },
}
</script>

<style>
.echope-product {
  background-color: #e7e7e7 !important;
}

.no-padding *:not(input, .dropdown-item, .dropdown-header, .feather, svg, label) {
  padding: 0 !important;
}

.no-padding .dropdown-item:active,
.no-padding .dropdown-item {
  background-color: white !important;
  cursor: default !important;
}

.no-padding svg.feather {
  stroke: #4b4953 !important;
  color: #4b4953 !important;
}

.no-padding .btn-outline-light {
  color: #4b4953 !important;
}

.no-padding .btn-outline-light:hover {
  color: #4b4953 !important;
}

.no-padding .feather,
.no-padding svg {
  pointer-events: auto !important;
  display: inline-block !important;
  z-index: 999999 !important;
}

.no-padding .formulate-input-label-container,
.no-padding .formulate-input-label {
  display: inline !important;
}

.b-popover {
  z-index: 9999 !important;
}
</style>
